"use client";

import { useState } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { PhoneAuthForm } from "./phone-auth-form";

/**
 * Standalone verification drawer, for the payment surfaces that do NOT use the
 * shared checkout sheet.
 *
 * The shared sheet hosts verification as one of its own views, which is right
 * there: it keeps the selected method and the pinned idempotency key alive
 * across the step. But not every payment entry point goes through that sheet —
 * the wallet's "Pay now" builds its own Razorpay checkout, and membership flows
 * have their own screens. Those still hit the same server gate, so they need
 * somewhere to put the recovery UI.
 *
 * Keeping the server gate universal and letting each UI choose its own
 * presentation is the whole point: a drawer-only solution would leave the
 * surfaces that never render the drawer simply broken. See PAY-02.
 */

/** Above sticky headers (z-1000) and the search overlays (z-9999). */
const VERIFICATION_LAYER = 10000;

export type PhoneVerificationDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** True when the account already has a number and this is a replacement. */
  isReplacement?: boolean;
  /**
   * Fired after the number is verified and recorded. Receives the canonical
   * E.164 value so a caller showing the number back can update without a
   * round trip.
   */
  onVerified: (phoneNumber: string) => void;
  title?: string;
  description?: string;
};

export function PhoneVerificationDrawer({
  open,
  onOpenChange,
  isReplacement = false,
  onVerified,
  title = "Verify your mobile number",
  description = "We'll text you a code. Verifying adds this number to the account you're already using for OTP sign-in and as your payment contact. It won't create a new account or subscribe you to promotions.",
}: PhoneVerificationDrawerProps) {
  const [phoneStep, setPhoneStep] = useState<"number" | "code">("number");
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setPhoneStep("number");
    onOpenChange(nextOpen);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
      repositionInputs={false}
    >
      <DrawerContent
        // `layer` sets the overlay too. A bare z-class lifts only the card and
        // leaves the dim at z-100, which every sticky header paints through.
        layer={VERIFICATION_LAYER}
        className="mx-auto max-h-[calc(100dvh-env(safe-area-inset-top)-8px)] w-full max-w-md rounded-t-[28px] border-border bg-surface text-text-primary dark:border-white/[0.12] dark:bg-[#111114] dark:text-white"
      >
        {phoneStep === "number" ? (
          <DrawerHeader className="px-4 pb-2 text-left">
            <DrawerTitle className="text-xl font-semibold tracking-tight">
              {title}
            </DrawerTitle>
            <DrawerDescription className="mt-1 text-sm">
              {description}
            </DrawerDescription>
          </DrawerHeader>
        ) : null}
        <div className="overflow-y-auto px-4 pt-2 pb-[calc(env(safe-area-inset-bottom)+1.25rem)]">
          {/* Mounted only while open, so the OTP field is created fresh each
              time rather than holding a stale code from a previous attempt. */}
          {open ? (
            <PhoneAuthForm
              mode={{ kind: "attach", isReplacement }}
              onStepChange={setPhoneStep}
              onSuccess={({ phoneNumber }) => {
                handleOpenChange(false);
                onVerified(phoneNumber);
              }}
              onCancel={() => handleOpenChange(false)}
            />
          ) : null}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
